"use client";

import { useRef } from "react";
import { SignUpButton } from "@clerk/nextjs";
import { ArrowLeftRight, Layers, MapPin, Store } from "lucide-react";
import { SignedOut } from "modules/auth/components/ClientAuthGate";
import { ClerkAvatar } from "modules/common/components/avatar/ClerkAvatar";
import { ICON_SIZE } from "modules/common/components/navigation/Menu";
import { useBottomBarInset } from "modules/common/hooks/useBottomBarInset";
import { useMediaQuery } from "modules/common/hooks/useMediaQuery";
import { cn } from "utils/utils";
import { useVisitorCtaEvents } from "./useVisitorCtaEvents";
import { FunnelContext, buildFunnelCtaLabel } from "./VisitorFunnelCopy";

interface VisitorCtaBarProps {
  context: FunnelContext;
  /**
   * Two placements, decided by one question: does the page have a Dock?
   *
   * `fixed` pins to the viewport bottom and publishes `--bottom-bar` so
   * `data-view-panel` ends above it — for Dock-less pages (profile, list, set,
   * city hub). `stacked` is the same bar in flow, passed to `<Dock header>`,
   * which wraps both in one fixed element and measures them together.
   *
   * Never mount more than one on a page, and never `fixed` on a page with a
   * Dock: two fixed elements, and `useBottomBarInset` publishes the taller
   * rather than the sum, so the grid would end under one of them.
   */
  variant: "fixed" | "stacked";
}

const MOBILE_QUERY = "(max-width: 767.98px)";

/** Only the fixed variant registers a bar; the stacked one is measured by Dock. */
const NULL_REF = { current: null };

const PLACE_ICONS = { city: MapPin, store: Store, set: Layers } as const;

/** Avatar where the context has a person, the context icon otherwise. */
function Identity({ context }: { context: FunnelContext }) {
  if (context.kind === "user" || context.kind === "list") {
    return (
      <ClerkAvatar
        preferredAvatar={context.preferredAvatar}
        username={
          context.kind === "user" ? context.username : context.ownerName
        }
        size="md"
        alt=""
      />
    );
  }
  const Icon = PLACE_ICONS[context.kind];
  return (
    <span className="border-base-content/15 bg-base-200 text-primary flex size-10 shrink-0 items-center justify-center rounded-full border">
      <Icon size={ICON_SIZE} />
    </span>
  );
}

/**
 * Mobile counterpart of <VisitorFunnelCard>: one row, identity on the left,
 * owner-anchored sign-up button on the right. Anonymous viewers only, and not
 * before Clerk resolves — the bar takes up layout, so showing it optimistically
 * would shift the page for every signed-in visitor.
 */
export function VisitorCtaBar(props: VisitorCtaBarProps) {
  return (
    <SignedOut waitForClerk>
      <Bar {...props} />
    </SignedOut>
  );
}

/**
 * Split from the gate so the bar's ref is attached by the time
 * `useBottomBarInset`'s layout effect runs. Gating inside this component would
 * mean rendering null on the first pass with the hooks already mounted, and the
 * effect (deps `[ref]`) would never re-run once the bar appeared — leaving
 * --bottom-bar unpublished and the grid's last row under the bar.
 */
function Bar({ context, variant }: VisitorCtaBarProps) {
  const isMobile = useMediaQuery(MOBILE_QUERY);
  const barRef = useRef<HTMLElement>(null);
  // The stacked variant sits in flow and must not publish --bottom-bar.
  useBottomBarInset(variant === "fixed" ? barRef : NULL_REF);

  const onCtaClick = useVisitorCtaEvents(context, variant, isMobile);

  return (
    <aside
      ref={barRef}
      aria-label="Join Lootstack to trade"
      className={cn(
        "border-base-300 bg-base-100 flex items-center gap-3 border-t px-3 pt-3 md:hidden",
        variant === "fixed"
          ? // Below AppLayout's z-[35] drawer, level with Dock.
            "fixed bottom-0 left-0 right-0 z-30 pb-[max(0.75rem,env(safe-area-inset-bottom))]"
          : // The Dock directly below already reserves the safe-area inset.
            "pb-3"
      )}
    >
      <Identity context={context} />
      <SignUpButton mode="modal">
        <button
          className="btn btn-primary min-w-0 flex-1 gap-2"
          onClick={onCtaClick}
        >
          <ArrowLeftRight size={ICON_SIZE} className="shrink-0" />
          <span className="truncate">{buildFunnelCtaLabel(context)}</span>
        </button>
      </SignUpButton>
    </aside>
  );
}
